import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  Badge,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as DenyIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { ptoRequestsApi } from '../services/api';
import { PTORequestWithDetails, PTORequestStatus, PTORequestReview } from '../types';

const PTOApprovals: React.FC = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [selectedRequest, setSelectedRequest] = useState<PTORequestWithDetails | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewAction, setReviewAction] = useState<'approved' | 'denied' | null>(null);

  // Fetch PTO requests
  const { data, isLoading, error } = useQuery(
    ['ptoRequests', statusFilter],
    () => ptoRequestsApi.getRequests({
      status: statusFilter !== 'all' ? statusFilter : undefined,
      page: 1,
      page_size: 100,
    })
  );

  // Get pending count
  const { data: pendingCountData } = useQuery(
    'pendingPTOCount',
    () => ptoRequestsApi.getPendingCount(),
    {
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  // Review mutation
  const reviewMutation = useMutation(
    ({ requestId, review }: { requestId: number; review: PTORequestReview }) =>
      ptoRequestsApi.reviewRequest(requestId, review),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('ptoRequests');
        queryClient.invalidateQueries('pendingPTOCount');
        handleCloseReviewDialog();
      },
    }
  );

  const handleOpenReviewDialog = (request: PTORequestWithDetails, action: 'approved' | 'denied') => {
    setSelectedRequest(request);
    setReviewAction(action);
    setReviewNotes('');
    setReviewDialogOpen(true);
  };

  const handleCloseReviewDialog = () => {
    setReviewDialogOpen(false);
    setSelectedRequest(null);
    setReviewAction(null);
    setReviewNotes('');
  };

  const handleSubmitReview = () => {
    if (!selectedRequest || !reviewAction) return;

    reviewMutation.mutate({
      requestId: selectedRequest.id,
      review: {
        status: reviewAction as PTORequestStatus,
        review_notes: reviewNotes || undefined,
      },
    });
  };

  const getStatusColor = (status: PTORequestStatus): "default" | "warning" | "success" | "error" => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'denied':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { timeZone: 'America/New_York' });
  };

  const formatPTOType = (type: string) => {
    return type.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">
          Error loading PTO requests. Please try again later.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <Badge badgeContent={pendingCountData?.pending_count || 0} color="error">
            PTO Approvals
          </Badge>
        </Typography>
        <TextField
          select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="all">All Requests</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="denied">Denied</MenuItem>
        </TextField>
      </Box>

      {reviewMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {(reviewMutation.error as any)?.response?.data?.detail ||
            'Failed to review request. Please try again.'}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell align="right">Days</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.requests && data.requests.length > 0 ? (
              data.requests.map((request) => (
                <TableRow key={request.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {request.employee_name || `${request.user.first_name} ${request.user.last_name}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {request.employee_name ? `Submitted by: ${request.user.first_name} ${request.user.last_name}` : request.user.employee_id}
                    </Typography>
                  </TableCell>
                  <TableCell>{request.user.department || '-'}</TableCell>
                  <TableCell>{formatPTOType(request.pto_type)}</TableCell>
                  <TableCell>
                    {formatDate(request.start_date)}
                    {request.is_half_day_start && (
                      <Chip label="½" size="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {formatDate(request.end_date)}
                    {request.is_half_day_end && (
                      <Chip label="½" size="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell align="right">{request.total_days.toFixed(1)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {request.reason || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.status.toUpperCase()}
                      color={getStatusColor(request.status)}
                      size="small"
                    />
                    {request.auto_approved && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Auto-approved
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(request.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      {request.status === 'pending' && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<ApproveIcon />}
                            onClick={() => handleOpenReviewDialog(request, 'approved')}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DenyIcon />}
                            onClick={() => handleOpenReviewDialog(request, 'denied')}
                          >
                            Deny
                          </Button>
                        </>
                      )}
                      {request.status !== 'pending' && (
                        <Typography variant="caption" color="text.secondary">
                          Reviewed by {request.reviewed_by?.first_name} {request.reviewed_by?.last_name}
                          {request.review_notes && (
                            <>
                              <br />
                              Notes: {request.review_notes}
                            </>
                          )}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No PTO requests found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={handleCloseReviewDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {reviewAction === 'approved' ? 'Approve' : 'Deny'} PTO Request
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Employee
                  </Typography>
                  <Typography variant="body2">
                    {selectedRequest.user.first_name} {selectedRequest.user.last_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Type
                  </Typography>
                  <Typography variant="body2">
                    {formatPTOType(selectedRequest.pto_type)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Start Date
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedRequest.start_date)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    End Date
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedRequest.end_date)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Total Days
                  </Typography>
                  <Typography variant="body2">
                    {selectedRequest.total_days.toFixed(1)} days
                  </Typography>
                </Grid>
                {selectedRequest.reason && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Reason
                    </Typography>
                    <Typography variant="body2">
                      {selectedRequest.reason}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Review Notes (Optional)"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            placeholder="Add any notes about this decision..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReviewDialog} disabled={reviewMutation.isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitReview}
            variant="contained"
            color={reviewAction === 'approved' ? 'success' : 'error'}
            disabled={reviewMutation.isLoading}
            startIcon={reviewMutation.isLoading ? <CircularProgress size={20} /> : null}
          >
            {reviewMutation.isLoading ? 'Processing...' : `Confirm ${reviewAction === 'approved' ? 'Approval' : 'Denial'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PTOApprovals;
